import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useDelayedFlag } from '@/shared/hooks';
import { defaultStaleTimeMs } from './gridDefaults';
import type { GridQueryInput, GridQueryResult } from './gridUtils';
import type { ApiError } from '@/shared/data/client';
import type { GridValidRowModel } from '@mui/x-data-grid';

type UseGridQueryLifecycleParams<T extends GridValidRowModel> = {
  finalQueryKey: QueryKey;
  token: string | null;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  initialData?: GridQueryResult<T>;
  queryInput: GridQueryInput;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | 'always';
  refetchIntervalMs?: number | false;
};

type UseGridQueryLifecycleResult<T extends GridValidRowModel> = {
  query: ReturnType<typeof useQuery<GridQueryResult<T>, ApiError>>;
  rowsData: T[];
  rowCount: number;
  showInitialSkeleton: boolean;
  lastUpdatedAt: number | null;
  queryIsFetching: boolean;
  queryIsFetched: boolean;
};

export const useGridQueryLifecycle = <T extends GridValidRowModel>({
  finalQueryKey,
  token,
  queryFn,
  initialData,
  queryInput,
  staleTimeMs,
  refetchOnWindowFocus,
  refetchOnMount,
  refetchIntervalMs,
}: UseGridQueryLifecycleParams<T>): UseGridQueryLifecycleResult<T> => {
  const query = useQuery<GridQueryResult<T>, ApiError>({
    queryKey: finalQueryKey,
    enabled: Boolean(token),
    queryFn: async (): Promise<GridQueryResult<T>> => {
      return await queryFn(queryInput);
    },
    placeholderData: (previous) => previous,
    initialData,
    staleTime: staleTimeMs ?? defaultStaleTimeMs,
    refetchOnWindowFocus: refetchOnWindowFocus ?? false,
    refetchOnReconnect: true,
    refetchOnMount: refetchOnMount ?? false,
    refetchInterval: refetchIntervalMs,
  });

  const emptyRows = useMemo<T[]>(() => [], []);
  const rowsData: T[] = query.data?.items ?? emptyRows;
  const hasFetchedOnceRef = useRef(Boolean(initialData));
  useEffect(() => {
    if (!query.isFetched) return;
    hasFetchedOnceRef.current = true;
  }, [query.isFetched]);
  useEffect(() => {
    if (token) return;
    hasFetchedOnceRef.current = Boolean(initialData);
  }, [initialData, token]);
  const lastRowCountRef = useRef<number>(0);
  useEffect(() => {
    if (!query.data) return;
    lastRowCountRef.current = query.data.total;
  }, [query.data]);
  const rowCount = query.data?.total ?? (query.isFetching ? lastRowCountRef.current : 0);
  const showInitialSkeleton = useDelayedFlag(query.isFetching && !hasFetchedOnceRef.current, 800);
  const lastUpdatedAt = query.dataUpdatedAt > 0 ? query.dataUpdatedAt : null;

  return {
    query,
    rowsData,
    rowCount,
    showInitialSkeleton,
    lastUpdatedAt,
    queryIsFetching: query.isFetching,
    queryIsFetched: query.isFetched,
  };
};
